// traceTooling.js: which product and surface ran a trace.
//
// A reviewer can see a trace is Claude and still not know whether it was the
// Claude Code CLI, the desktop app's Code tab, Cowork, an IDE extension, or an
// SDK harness. No single captured field says so; this module reads the
// evidence the capture happens to carry, most authoritative first:
//
//   1. The billing header Claude Code prepends to its system prompt
//      (`x-anthropic-billing-header: cc_version=...; cc_entrypoint=cli; ...`).
//      `cc_entrypoint` is the harness's own statement of its surface. Values
//      observed: `cli`, `claude-desktop`, `sdk-cli`, `local-agent` (Cowork -
//      every capture carrying it also offered the `mcp__cowork__*` tools).
//      Only captures that hold the system prompt have it (OTLP request
//      bodies do; transcript tailers do not).
//   2. Framing in the system prompt and the tool surface the model was
//      offered: "Claude desktop app (Code tab)" names the desktop Code tab,
//      `mcp__cowork__*` tools exist only in Cowork, `mcp__ide__*` only with an
//      IDE attached. A capture without the offered tools (a transcript tailer
//      records calls, not definitions) is read as Cowork off the tools the
//      model called.
//   3. The process that launched the session (image name + command line from
//      the endpoint's OS telemetry). Install paths separate the surfaces:
//      the desktop app downloads its Claude Code under
//      `Application Support/Claude/claude-code` (`AppData\Roaming\Claude` on
//      Windows), the VS Code extension bundles a native binary under
//      `.vscode/extensions/anthropic.claude-code-*`, the Python Agent SDK under
//      `claude_agent_sdk/_bundled`, and a bare `claude` / `claude.exe`
//      (Homebrew cask, `.local/bin`, `.local/share/claude/versions`) is the CLI.
//   4. The client surface the capture producer stamped (for example
//      `copilot-vscode`), which names product and surface for tools whose
//      provider tag is shared with another product.
//   5. The provider tag, which names the vendor and nothing more.
//
// Mapped entrypoints and stamps retain their typed qualifiers. For legacy
// heuristics, lower sources fill what higher ones left unsaid: a desktop-bundled
// process plus Cowork tools reads "Claude Cowork - Desktop app" and the same
// process with a `claude-desktop` entrypoint reads "Claude Code - Desktop app
// (Code tab)". Every contribution is kept as evidence for the tooltip.

import { sourceClientSummary, clientSurfaceIdentity, deriveAgentIdentity, inferenceProviderSummaryLabel } from "./agentIdentity.js";
import { CLAUDE_ENTRYPOINT_SURFACES, clientSurfaceLabel, harnessIdentity } from "./clientSurfaceIdentity.js";
import { HARNESS_UNKNOWN_LABEL } from "./harnessCatalog.js";
import { evidenceValueLabel, evidenceDimensionLines, identityEvidenceDimensions, evidenceProvenanceLines } from "./sourceEvidence.js";

// `cc_entrypoint` values and what they mean. Unknown values are shown
// verbatim rather than dropped: a new surface is still a fact worth reading.
export const ENTRYPOINT_SURFACES = Object.fromEntries(Object.entries(CLAUDE_ENTRYPOINT_SURFACES).map(([entrypoint, id]) => {
  const identity = clientSurfaceIdentity(id);
  return [entrypoint, { product: identity.name, surface: evidenceValueLabel("host", identity.host) }];
}));

// Verified browser identity supplied by the native-messaging bridge.
export function browserFamilyLabel(family) {
  return family.charAt(0).toUpperCase() + family.slice(1);
}

// Launch-path rules, most specific first. Paths are lowercased with both
// slash styles kept, so one rule covers macOS and Windows spellings.
const PROCESS_RULES = [
  {
    re: /(application support|appdata[\\/]roaming)[\\/]claude[\\/]claude-code[\\/]/,
    product: "Claude Code", surface: "Desktop app", detail: "desktop-bundled Claude Code",
  },
  {
    re: /[\\/]claude\.app[\\/]contents[\\/]|claude helper|anthropicclaude/,
    product: "Claude", surface: "Desktop app", detail: "Claude desktop app process",
  },
  {
    re: /\.vscode[\\/]extensions[\\/]anthropic\.claude-code/,
    product: "Claude Code", surface: "VS Code extension", detail: "VS Code extension binary",
  },
  {
    re: /claude_agent_sdk[\\/]_bundled|@anthropic-ai[\\/]claude-agent-sdk/,
    product: "Claude Code", surface: "Agent SDK", detail: "Agent SDK bundled binary",
  },
  {
    re: /\.local[\\/]share[\\/]claude[\\/]versions[\\/]|caskroom[\\/]claude-code|@anthropic-ai[\\/]claude-code[\\/]|[\\/]claude(\.exe)?(\s|$)/,
    product: "Claude Code", surface: "CLI", detail: "claude binary",
  },
  {
    // A shell that runs `claude` itself (a script, `bash -c "claude -p ..."`):
    // the session's process is the shell, and the binary is only in its
    // command.
    re: /(^|[\\/])(ba|z|da|fi|k|c|tc)?sh(\.exe)?(\s|$)|(^|[\\/])(pwsh|powershell|cmd)(\.exe)?(\s|$)/,
    cmd: /(^|[\s;&|("'`])claude(\.exe)?(\s|$)/,
    product: "Claude Code", surface: "CLI", detail: "claude run from a shell",
  },
  {
    // The Copilot CLI installs as a bare `copilot` binary or the
    // `@github/copilot` npm package. Microsoft's consumer Copilot app also
    // ships a `Copilot` executable (a WindowsApps package, a macOS bundle),
    // so those install locations are excluded rather than misread as the CLI.
    re: /@github[\\/]copilot[\\/]|[\\/]copilot(\.exe)?(\s|$)/,
    exclude: /windowsapps|microsoft\.copilot|copilot\.app[\\/]/,
    product: "GitHub Copilot", surface: "CLI", detail: "copilot binary",
  },
  {
    re: /codex computer use\.app|codex-computer-use|skycomputeruseclient/,
    product: "Codex", surface: "Desktop app", detail: "Codex computer-use client",
  },
  {
    re: /[\\/]codex(\.exe)?(\s|$)/, cmd: /app-server/,
    product: "Codex", surface: "App server", detail: "codex app-server (desktop or IDE host)",
  },
  {
    re: /[\\/]codex(\.exe)?(\s|$)/,
    product: "Codex", surface: "CLI", detail: "codex binary",
  },
];

function systemText(turns) {
  const out = [];
  for (const turn of turns || []) {
    if (turn?.role !== "system") continue;
    for (const block of turn.blocks || []) {
      if (block?.type === "text" && typeof block.text === "string") out.push(block.text);
    }
  }
  return out.join("\n");
}

function offeredToolNames({ tools, toolDefs, turns }) {
  const names = new Set();
  for (const t of tools || []) if (typeof t === "string") names.add(t);
  for (const d of toolDefs || []) if (d?.name) names.add(d.name);
  for (const turn of turns || []) {
    if (turn?.role !== "system") continue;
    for (const block of turn.blocks || []) if (block?.type === "tool_def" && block.name) names.add(block.name);
  }
  return [...names];
}

function calledToolNames(turns) {
  const names = new Set();
  for (const turn of turns || []) {
    if (turn?.role === "system") continue;
    for (const block of turn.blocks || []) if (block?.type === "tool_use" && block.name) names.add(block.name);
  }
  return [...names];
}

// `x-anthropic-billing-header: cc_version=2.1.260.118; cc_entrypoint=cli; cch=00000`
// -> { cc_version, cc_entrypoint, cch }. Null when the header is absent.
export function parseBillingHeader(text) {
  const m = /x-anthropic-billing-header:\s*([^\n"\\]+)/i.exec(text || "");
  if (!m) return null;
  const fields = {};
  for (const part of m[1].split(";")) {
    const eq = part.indexOf("=");
    if (eq < 0) continue;
    const key = part.slice(0, eq).trim();
    const value = part.slice(eq + 1).trim();
    if (key && value) fields[key] = value;
  }
  return Object.keys(fields).length ? fields : null;
}

// "2.1.260.118" -> "2.1.260": the fourth component is a build stamp.
function shortVersion(v) {
  const m = /^(\d+\.\d+\.\d+)/.exec(String(v || ""));
  return m ? m[1] : (v ? String(v) : null);
}

function normalizePath(s) {
  return String(s || "").toLowerCase();
}

export function processSurface(origin) {
  if (!origin) return null;
  const image = normalizePath(origin.imageName);
  const command = normalizePath(origin.commandLine);
  const haystack = `${image} ${command}`;
  for (const rule of PROCESS_RULES) {
    if (!rule.re.test(haystack)) continue;
    if (rule.exclude && rule.exclude.test(haystack)) continue;
    if (rule.cmd && !rule.cmd.test(command)) continue;
    return { product: rule.product, surface: rule.surface, detail: rule.detail };
  }
  return null;
}

// The browser family a trace's browser observations share; "browser" when
// they come from more than one.
export function traceBrowserFamily(trace) {
  const families = [...new Set((trace?.sourceObservations || []).map(observation => observation?.browserFamily).filter(Boolean))];
  return families.length > 1 ? "browser" : (families[0] || "");
}

// What deriveTraceTooling reads off a trace (or a record's meta, which carries
// a subset), so every view of one trace weighs the same evidence. `turns` is
// passed separately because views hold them apart from the trace.
//
// `structuredOnly` keeps to the capture's client evidence: a list row's
// Harness column must not promote a provider routing tag or a launch process
// to a harness the server's verdict did not name.
export function toolingInputs(trace, { turns = null, structuredOnly = false } = {}) {
  const structured = {
    clientSurface: trace?.clientSurface || "",
    clientAttributions: trace?.clientAttributions ?? null,
    clientAttributionsTruncated: Boolean(trace?.clientAttributionsTruncated),
    clientEvidenceState: trace?.clientEvidenceState ?? null,
  };
  if (structuredOnly) return structured;
  return {
    ...structured,
    turns,
    tools: trace?.tools ?? null,
    toolDefs: trace?.toolDefs ?? null,
    sessionOrigin: trace?.sessionOrigin || null,
    provider: trace?.provider || "",
    browserFamily: traceBrowserFamily(trace),
  };
}

// A list row's Harness cell: the server's category when it named one, else
// the row's structured client evidence, else the catalog's unknown label.
export function traceHarnessView(trace) {
  const tooling = deriveTraceTooling(toolingInputs(trace, { structuredOnly: true }));
  if (!trace?.harness) return { label: tooling.label || HARNESS_UNKNOWN_LABEL, tooling };
  const label = harnessIdentity(trace.harness).name;
  return { label, tooling: { ...tooling, label, product: label } };
}

// -> { product, surface, version, ide, known, evidence, label }
//   product   what ran ("Claude Code", "Claude Cowork", "Codex", or the
//             provider's brand when nothing finer is known)
//   surface   how it ran ("CLI", "Desktop app, Code tab", ...) or null
//   version   the harness version when stated, else null
//   ide       true when an IDE was attached (mcp__ide__* tools offered)
//   known     true when anything beyond the provider tag contributed
//   evidence  [{ source, detail }] in precedence order, for the tooltip
//   label     one compact string for the strip
export function deriveTraceTooling({ turns = null, tools = null, toolDefs = null, sessionOrigin = null, provider = "", agentType = undefined, clientSurface = "", browserFamily = "", clientAttributions = null, clientAttributionsTruncated = false, clientEvidenceState = null } = {}) {
  if (clientEvidenceState === "unavailable") return { structured: true, product: "Client evidence unavailable", label: "Client evidence unavailable", evidence: [{ source: "Evidence", detail: "Evidence could not be loaded. Refresh to try again." }], inferenceProvider: "evidence unavailable", qualifiers: [] };
  const attributed = sourceClientSummary(clientAttributions, { truncated: clientAttributionsTruncated });
  if (attributed) {
    const evidence = attributed.surfaces.map(label => ({ source: "Client surface", detail: label }));
    if (attributed.unavailable || attributed.truncated) evidence.push({ source: "Client evidence", detail: "Some displayed observations do not have a resolved client surface." });
    if (attributed.conflicting) evidence.push({ source: "Client evidence", detail: "Observed client facts conflict; a single surface cannot be selected." });
    for (const observation of attributed.observations.slice(0, 8)) {
      for (const candidate of observation.clientEvidence) {
        evidence.push({ source: clientSurfaceLabel(candidate.identity), detail: evidenceProvenanceLines([candidate]).join("; ") });
      }
    }
    return { structured: true, observations: attributed.observations, truncated: attributed.truncated, product: attributed.label, surface: null, version: null, ide: false,
      known: attributed.surfaces.length > 0, evidence, label: attributed.label,
      inferenceProvider: inferenceProviderSummaryLabel(attributed), dimensions: attributed.dimensions,
      qualifiers: evidenceDimensionLines(attributed.dimensions) };
  }
  const evidence = [];
  const sys = systemText(turns);
  const names = offeredToolNames({ tools, toolDefs, turns });

  let product = null;
  let surface = null;
  let version = null;
  let surfaceId = null;
  const claim = (source, detail, fields) => {
    evidence.push({ source, detail });
    if (fields.product && !product) product = fields.product;
    if (fields.surface && !surface && !surfaceId) surface = fields.surface;
    if (fields.version && !version) version = fields.version;
  };

  const billing = parseBillingHeader(sys);
  if (billing) {
    const ep = billing.cc_entrypoint || "";
    const known = Object.hasOwn(ENTRYPOINT_SURFACES, ep) ? ENTRYPOINT_SURFACES[ep] : null;
    const fields = known
      ? { ...known }
      : (ep ? { product: "Claude Code", surface: ep } : { product: "Claude Code" });
    fields.version = shortVersion(billing.cc_version);
    claim("billing header", [ep && `cc_entrypoint=${ep}`, billing.cc_version && `cc_version=${billing.cc_version}`].filter(Boolean).join(", ") || "present", fields);
    if (known) surfaceId = CLAUDE_ENTRYPOINT_SURFACES[ep];
  }

  if (/claude desktop app \(code tab\)/i.test(sys)) {
    claim("system prompt", "\"running inside the Claude desktop app (Code tab)\"", { product: "Claude Code", surface: "Desktop app, Code tab" });
  } else if (/you are claude code/i.test(sys)) {
    claim("system prompt", "\"You are Claude Code\"", { product: "Claude Code" });
  }

  const called = calledToolNames(turns);
  const isCowork = (n) => /^mcp__cowork(__|-)/.test(n);
  if (names.some(isCowork)) {
    claim("tools offered", "mcp__cowork__* tools", { product: "Claude Cowork", surface: "Desktop app" });
  } else if (called.some(isCowork)) {
    claim("tools called", "mcp__cowork__* tools", { product: "Claude Cowork", surface: "Desktop app" });
  }
  const ide = names.some((n) => /^mcp__ide__/.test(n));
  if (ide) evidence.push({ source: "tools offered", detail: "mcp__ide__* tools: an IDE was attached" });

  const surfaceIdentity = clientSurfaceIdentity(clientSurface);
  if (surfaceIdentity) {
    claim("client surface", String(clientSurface).trim(), { product: surfaceIdentity.name, surface: evidenceValueLabel("host", surfaceIdentity.host) });
    if (!surfaceId && product === surfaceIdentity.name && surface === evidenceValueLabel("host", surfaceIdentity.host)) surfaceId = clientSurface;
  }

  // Claude Code's own system prompt opens its environment section this way. A
  // capture that keeps only part of the system prompt (OTLP's) has no billing
  // header or "You are Claude Code" line to go on, but keeps this.
  if (!product && /you have been invoked in the following environment/i.test(sys)) {
    claim("system prompt", "\"You have been invoked in the following environment\"", { product: "Claude Code" });
  }

  if (sessionOrigin && (sessionOrigin.imageName || sessionOrigin.commandLine)) {
    const proc = processSurface(sessionOrigin);
    const image = String(sessionOrigin.imageName || "").split(/[\\/]/).pop() || String(sessionOrigin.commandLine || "").split(/\s+/)[0];
    if (proc) {
      claim("launch process", `${proc.detail}: ${image}`, { product: proc.product, surface: proc.surface });
    } else {
      const identity = deriveAgentIdentity(agentType, sessionOrigin.commandLine, sessionOrigin.imageName, sessionOrigin.provider || provider, clientSurface);
      claim("launch process", image, { product: identity?.key !== "unknown" ? identity.name : null });
    }
  }

  const known = evidence.length > 0;
  if (!product) {
    const identity = deriveAgentIdentity(agentType, "", "", provider, clientSurface);
    if (identity && identity.key !== "unknown") {
      product = identity.name;
      evidence.push({ source: "provider", detail: String(provider || agentType || "") });
    }
  }
  // A browser chat whose app was not reported still ran in a known browser.
  // Preserve the reported browser identity.
  if (product && !surface && !surfaceId && browserFamily) {
    claim("browser extension", `${browserFamily} family`, { surface: browserFamilyLabel(browserFamily) });
  }

  const identity = clientSurfaceIdentity(surfaceId);
  if (identity?.interface && surface === evidenceValueLabel("interface", identity.interface)) surface = null;
  // Lower-priority evidence can fill missing fields, but must not relabel a different identity.
  if (identity && (identity.name !== product || evidenceValueLabel("host", identity.host) !== surface)) surfaceId = null;
  const label = surfaceId ? clientSurfaceLabel(surfaceId, { version, ide }) : product
    ? [`${product}${version ? ` ${version}` : ""}`, surface, ide ? "IDE attached" : null].filter(Boolean).join(" \u00b7 ")
    : "";
  const dimensions = identityEvidenceDimensions(clientSurfaceIdentity(surfaceId), {
    name: billing?.cc_entrypoint ? "request.anthropic.cc_entrypoint" : "client_surface",
    stringValue: billing?.cc_entrypoint || clientSurface,
    provenance: { sourcePath: billing?.cc_entrypoint ? "billing_header.cc_entrypoint" : "client_surface" },
  });
  return { product, surface, version, ide, known, evidence, label, surfaceId, dimensions,
    qualifiers: evidenceDimensionLines(dimensions) };
}
